import { defineStore } from "pinia";
import { ApiClient } from "@/api-client";
import { i18n } from "@/locales/index";
import { useSystemStore, useApplicationStore } from "./index";
import * as ApiResponse from "@/api-client/responses";
import { watch } from "vue";

const apiClient = ApiClient.getInstance();

export const useSupplierStore = defineStore("supplierStore", {
  state: () => ({
    suppliers: [] as ApiResponse.Suppliers.GetSuppliers,
    loadingState: "unset" as loadingStateType,
    failedReason: "",
    selectedSupplier: null as ApiResponse.Suppliers.Supplier | null,
  }),
  actions: {
    setSelectedSupplier(
      supplier: ApiResponse.Suppliers.Supplier | undefined | null
    ) {
      this.selectedSupplier = supplier ?? null;
    },
    async getSuppliers(): Promise<void> {
      // get needed stores
      const systemStore = useSystemStore();

      // check if the user selected a system
      if (!systemStore.selectedSystem) throw new Error("No system selected");

      // set loading state and get data
      this.loadingState = "loading";
      try {
        this.suppliers = await apiClient.getSupplierList(
          systemStore.selectedSystem.supplierId,
          systemStore.selectedSystem.system
        );
      } catch (error) {
        // handle error
        this.loadingState = "failed";
        this.failedReason = i18n.global.t("app.supplier.loadingData.error", {
          message: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }

      // select first one if there is only one selectable
      if (this.suppliers.length == 1)
        this.setSelectedSupplier(this.suppliers[0]);

      // check if no suppliers in response
      if (this.suppliers.length === 0) {
        useApplicationStore().addAlert({
          type: "Warning",
          key: "app.supplier.loadingData.empty",
        });
      }

      // set state
      this.loadingState = "success";
    },
    initWatcher() {
      // get supplier list after user selected a system
      watch(
        () => useSystemStore().selectedSystem,
        async (system) => {
          const applicationStore = useApplicationStore();
          // system was set null
          if (!system) {
            this.suppliers = [];
            this.selectedSupplier = null;
            return;
          }

          // get suppliers
          try {
            await this.getSuppliers();
          } catch (error) {
            // handle error
            applicationStore.addAlert({
              type: "Alert",
              key: "app.supplier.loadingData.error",
              keyData: {
                message: error instanceof Error ? error.message : String(error),
              },
            });
            return;
          }
        }
      );
    },
    async handleCsvDownloadButtonClick() {
      // get needed stores
      const systemStore = useSystemStore();
      const applicationStore = useApplicationStore();

      // check if needed variables were set
      if (!systemStore.selectedSystem) return;
      if (!this.selectedSupplier) return;

      // download file
      try {
        await apiClient.getCsvFile({
          system: systemStore.selectedSystem,
          supplier: this.selectedSupplier,
        });
      } catch (error) {
        // handle error
        applicationStore.addAlert({
          type: "Alert",
          key: "app.supplier.csvData.loadingData.failed",
          keyData: {
            supplier: this.selectedSupplier.Name,
          },
        });
      }
    },
  },
});
